import { forwardRef } from 'react'

import { regex } from '../../utils/validation'
import { ArcadeHelpTextComponentProps } from '../Arcade/Input/ArcadeHelpText'
import ArcadeInput from '../Arcade/Input/ArcadeInput'
import FormattedInput, { BaseFormattedInputProps } from '../Arcade/Input/FormattedInput'

export type PostalCodeSupportedRegions = 'US' | 'CA' | 'GB' | 'AU'

export interface PostalCodeInputProps extends BaseFormattedInputProps, ArcadeHelpTextComponentProps {
  /** Specific region for postal code formatting; if not provided, defaults to all supported regions */
  region?: PostalCodeSupportedRegions
}

export const postalCodeRegionConfig = {
  AU: {
    format: '#'.repeat(4),
    regex: regex.auPostcode,
  },
  CA: {
    format: 'A#A #A#',
    regex: regex.caPostcode,
  },
  GB: {
    format: 'AA## #AA',
    regex: regex.gbPostcode,
  },
  US: {
    format: '#'.repeat(5),
    regex: regex.usPostcode,
  },
} as const

/**
 * Input for postal codes with region-specific formatting and validation.
 * Supports AU, CA, GB, and US regions with appropriate format templates.
 * If no region is specified, it accepts postal codes from all supported regions.
 *
 * @example
 * ```tsx
 * <FieldLabel label="Postal Code">
 *   <PostalCodeInput
 *     region="US"
 *     value={value}
 *     onChange={setValue}
 *     placeholder="12345"
 *   />
 * </FieldLabel>
 * ```
 */
export const PostalCodeInput = forwardRef<HTMLInputElement, PostalCodeInputProps>(
  ({ value, region, placeholder, onChange, ...props }, ref) => {
    const defaultPlaceholder = region ? postalCodeRegionConfig[region].format : postalCodeRegionConfig.US.format

    const sharedProps = {
      ...props,
      placeholder: placeholder || defaultPlaceholder,
      autoComplete: 'postal-code',
      ref,
    }

    if (region === 'AU' || region === 'US') {
      return (
        <FormattedInput
          {...sharedProps}
          template={postalCodeRegionConfig[region].format}
          onChange={onChange}
          value={value}
        />
      )
    }

    return (
      <ArcadeInput
        {...sharedProps}
        // Defaults to 10 as longest postal codes are 10 characters
        maxLength={region ? postalCodeRegionConfig[region].format.length : 10}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          onChange(event.target.value.toUpperCase())
        }}
        onInput={(event: React.FormEvent<HTMLInputElement>) => {
          event.currentTarget.value = event.currentTarget.value.toUpperCase()
        }}
        type="text"
      />
    )
  }
)

export default PostalCodeInput
